import './index.css'
import cn from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'
type AnimationState = 'idle' | 'measuring' | 'animating'
type Props = {
  children: React.ReactNode
  open: boolean
  scrollIntoView?: boolean
  adjustedContentHeight?: string | number
}

/**
 * An animated collapsible container that smoothly expands and collapses content
 * @param children - The content to be shown/hidden within the collapsible container
 * @param open - Whether the collapsible is currently open (expanded)
 * @param scrollIntoView - Whether to scroll the element into view after animation completes - defaults to false
 * @param adjustedContentHeight - Optional fixed height to use instead of auto-calculated height
 * @returns A div element that animates between collapsed and expanded states
 */
const Collapsible = ({
  children,
  open,
  scrollIntoView,
  adjustedContentHeight,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null | undefined>(null)
  const [animationState, setAnimationState] = useState<AnimationState>('idle')
  const [height, setHeight] = useState<number | string | null | undefined>(
    open ? undefined : 0,
  )
  const [isOpen, setIsOpen] = useState<boolean>(open)
  useEffect(() => {
    if (open !== isOpen) {
      setAnimationState('measuring')
    }
  }, [open, isOpen])
  useEffect(() => {
    if (!containerRef.current) return
    requestAnimationFrame(() => {
      switch (animationState) {
        case 'idle':
          break

        case 'measuring':
          setAnimationState('animating')
          setHeight(open ? 0 : containerRef.current?.scrollHeight)
          break

        case 'animating':
          if (open) {
            setHeight(
              adjustedContentHeight ?? containerRef.current?.scrollHeight ?? 0,
            )
          } else {
            setHeight(0)
          }

          setIsOpen(open)
      }
    })
  }, [animationState, open])
  const handleTransitionEnd = useCallback(
    (event: React.SyntheticEvent<any>) => {
      if (event.target === containerRef.current) {
        setAnimationState('idle')
        setHeight(open ? undefined : 0)

        if (scrollIntoView) {
          scrollIntoViewIfNeeded(containerRef.current, {
            behavior: 'smooth',
            scrollMode: 'if-needed',
          })
        }
      }
    },
    [open, scrollIntoView],
  )
  return (
    <div
      className={cn('Collapsible', {
        'Collapsible--animating': animationState === 'animating',
        'Collapsible--open': isOpen,
      })}
      ref={containerRef}
      style={{
        height,
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={cn({
          'Collapsible-content--hidden':
            animationState === 'idle' && !open && !isOpen,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default React.memo<Props>(Collapsible)
